/**
 * Platform detection for UI affordances only — never for feature gating.
 *
 * Exists because macOS reserves Ctrl+click as the OS right-click, so every
 * "Ctrl + drag" hint in the viewer is a lie on a Mac: following it opens the
 * context menu. The tool layer binds BOTH Ctrl and Meta (Cmd); this helper
 * only decides which one the hint text shows.
 */
export function isMacPlatform() {
    if (typeof navigator === 'undefined')
        return false;
    // userAgentData is the modern source; platform still works everywhere else.
    const nav = navigator;
    const p = nav.userAgentData?.platform ?? navigator.platform ?? '';
    return /mac|iphone|ipad|ipod/i.test(p);
}
/** The modifier label the current platform's users actually press. */
export function modKeyLabel() {
    return isMacPlatform() ? '\u2318' : 'Ctrl';
}
/**
 * iPhone, iPad or iPod, i.e. iOS/iPadOS WebKit, where a Safari tab is killed
 * at a per-tab memory ceiling well below device RAM and navigator.deviceMemory
 * does not exist. iPadOS Safari requests the desktop site and reports a Mac UA,
 * so an iPad is told apart from a Mac by its touch points: a real iPad reports
 * 5, a Mac 0. The test is > 1, not > 0: Chromium's touch emulation (DevTools
 * device mode, Playwright's hasTouch) reports 1 under a Mac UA, and that is
 * not iPadOS WebKit. One of the documented cases where reading the UA is the
 * only signal.
 */
export function isAppleTouchDevice() {
    if (typeof navigator === 'undefined')
        return false;
    const ua = navigator.userAgent ?? '';
    if (/iPhone|iPad|iPod/.test(ua))
        return true;
    return /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
}
/** A phone-sized screen: its shorter side is under 600 CSS px in either orientation. */
export function isPhoneSizedScreen() {
    if (typeof screen === 'undefined')
        return false;
    return Math.min(screen.width, screen.height) < 600;
}
/**
 * True when the PRIMARY pointer is coarse (a finger): phones, tablets, a
 * Surface in tablet mode. A Windows touch laptop driven by its trackpad is not,
 * which is the point: this decides focus and keyboard behaviour, not whether a
 * touchscreen exists at all (use `(any-pointer: coarse)` for that).
 */
export function isCoarsePointer() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        return false;
    try {
        return window.matchMedia('(pointer: coarse)').matches;
    }
    catch {
        return false;
    }
}
